// components/Admin/MiniroomManager/BackgroundFormModal.jsx
import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Grid, FormControlLabel, Checkbox, Select, MenuItem, InputAdornment
} from "@mui/material";
import api from "../../../api/axios";

export default function BackgroundFormModal({ open, onClose, onSuccess, initial }) {
    const [submitting, setSubmitting] = useState(false);

    const [name, setName] = useState("");
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);

    const [price, setPrice] = useState(0);
    const [isUnlimited, setIsUnlimited] = useState(true);
    const [stock, setStock] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [isFree, setIsFree] = useState(false);

    // ▼ 추가: 배경 맞춤/배율/오프셋
    const [fitMode, setFitMode] = useState("cover");   // "cover" | "contain" | "stretch"
    const [scale, setScale] = useState(1);             // 배율(1 = 100%)
    const [offsetX, setOffsetX] = useState(0);         // px
    const [offsetY, setOffsetY] = useState(0);         // px
    const previewSrc = preview || initial?.image_url || null;

    // initial 프리필
    useEffect(() => {
        if (!open) return;
        if (initial) {
            setName(initial.name ?? "");
            setImage(null);
            setPreview(initial.image_url ?? null);
            setPrice(initial.price ?? 0);
            setIsUnlimited(!!initial.is_unlimited);
            setStock(initial.stock ?? 0);
            setIsActive(!!initial.is_active);
            setIsFree(!!initial.is_free);


            // ▼ 추가: 서버 읽기용 필드 프리필
            setFitMode(initial.fit_mode ?? "cover");
            setScale(Number.isFinite(initial.scale) ? Number(initial.scale) : 1);
            setOffsetX(Number.isFinite(initial.offset_x) ? Number(initial.offset_x) : 0);
            setOffsetY(Number.isFinite(initial.offset_y) ? Number(initial.offset_y) : 0);
        } else {
            reset();
        }
    }, [open, initial]);

    const toInt = (v, d = 0) => {
        const n = Number(v);
        return Number.isFinite(n) ? Math.trunc(n) : d;
    };
    const nonneg = (v) => Math.max(0, toInt(v, 0));
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

    const pickImage = (file) => {
        if (!file) return;
        if (!file.type?.startsWith("image/")) {
            alert("이미지 파일만 업로드할 수 있습니다.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert("이미지 용량이 너무 큽니다. 5MB 이하로 업로드해주세요.");
            return;
        }
        setImage(file);
    };

    // 새 파일 선택 시에만 미리보기 교체
    useEffect(() => {
        if (!image) return;
        const url = URL.createObjectURL(image);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [image]);

    useEffect(() => {
        if (isUnlimited) setStock(0);
    }, [isUnlimited]);

    const reset = () => {
        setName("");
        setImage(null); setPreview(null);
        setPrice(0); setIsUnlimited(true); setStock(0);
        setIsActive(true); setIsFree(false);
        setFitMode("cover");
        setScale(1);
        setOffsetX(0);
        setOffsetY(0);
    };

    const handleSubmit = async () => {
        if (submitting) return;
        if (!name.trim()) { alert("이름을 입력해 주세요."); return; }
        if (!initial?.id && !image) { alert("이미지를 업로드해 주세요."); return; }

        // 간단 검증(선택)
        const safeScale = clamp(Number(scale) || 1, 0.1, 5); // 10% ~ 500%
        const safeOffsetX = toInt(offsetX, 0);
        const safeOffsetY = toInt(offsetY, 0);

        setSubmitting(true);
        const fd = new FormData();
        fd.append("name", name);
        if (image) fd.append("image", image);
        fd.append("price", isFree ? 0 : nonneg(price));
        fd.append("is_unlimited", isUnlimited ? "true" : "false");
        fd.append("stock", isUnlimited ? 0 : nonneg(stock));
        fd.append("is_active", isActive ? "true" : "false");
        fd.append("is_free", isFree ? "true" : "false");

        // ▼ 추가: 맞춤/배율/오프셋 전송 (서버 필드명과 일치)
        fd.append("fit_mode", fitMode);                // "cover" | "contain" | "stretch"
        fd.append("scale", String(safeScale));         // FloatField
        fd.append("offset_x", String(safeOffsetX));    // IntegerField
        fd.append("offset_y", String(safeOffsetY));    // IntegerField

        try {
            if (initial?.id) {
                await api.patch(`/api/miniroom/admin/backgrounds/${initial.id}/`, fd, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
            } else {
                await api.post("/api/miniroom/admin/backgrounds/", fd, {
                    headers: { "Content-Type": "multipart/form-data" }
                });
            }
            onSuccess?.();
            reset();
            onClose();
        } catch (e) {
            const data = e?.response?.data;
            const detail = typeof data === "string" ? data : (data ? JSON.stringify(data, null, 2) : e.message);
            console.error(data || e);
            alert(`등록 실패\n${detail}`);
        } finally {
            setSubmitting(false);
        }
    };

    const disabled =
        !name.trim() ||
        (!initial?.id && !image) ||
        (!isUnlimited && nonneg(stock) <= 0);

    return (
        <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle>{initial?.id ? "배경 수정" : "배경 등록 (벽+바닥 일체형)"}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} mt={0.5}>
                    <Grid item xs={12}>
                        <TextField label="이름" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
                    </Grid>

                    <Grid item xs={12}>
                        <Button variant="outlined" component="label" fullWidth>
                            이미지 업로드{initial?.id ? " (교체 시 선택)" : " (필수)"}
                            <input hidden type="file" accept="image/*" onChange={(e) => pickImage(e.target.files?.[0])} />
                        </Button>
                        <div style={{
                            marginTop: 8, border: "1px solid #e0e0e0", borderRadius: 8,
                            height: 160, position: "relative", background: "#fafafa", overflow: "hidden"
                        }}>
                            {previewSrc ? (
                                <img
                                    src={previewSrc}
                                    alt="background preview"
                                    style={{
                                        position: "absolute",
                                        left: "50%", top: "50%",
                                        width: "100%", height: "100%",
                                        objectFit: fitMode === "cover" ? "cover" : fitMode === "contain" ? "contain" : "fill",
                                        transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) scale(${Number(scale) || 1})`,
                                        transformOrigin: "center center",
                                        imageRendering: "pixelated",
                                        pointerEvents: "none", userSelect: "none",
                                    }}
                                    draggable={false}
                                />
                            ) : (
                                <span style={{
                                    color: "#999", fontSize: 12, position: "absolute", inset: 0,
                                    display: "flex", alignItems: "center", justifyContent: "center"
                                }}>
                                    미리보기 없음
                                </span>
                            )}
                        </div>
                    </Grid>

                    {/* 가격/무료/판매/재고/노출 */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="가격(Leaf)"
                            type="number"
                            value={price}
                            onChange={(e) => setPrice(nonneg(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                            disabled={isFree}
                            helperText={isFree ? "무료 배경은 가격이 0으로 고정됩니다." : ""}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />}
                            label="모든 유저 무료 사용(is_free)"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Checkbox checked={isUnlimited} onChange={(e) => setIsUnlimited(e.target.checked)} />}
                            label="무제한 판매"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="재고"
                            type="number"
                            value={stock}
                            onChange={(e) => setStock(nonneg(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                            disabled={isUnlimited}
                            helperText={isUnlimited ? "무제한 판매에서는 재고가 사용되지 않습니다." : ""}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                            label="상점/목록 노출(is_active)"
                        />
                    </Grid>

                    {/* ▼ 추가: 맞춤/배율/오프셋 UI */}
                    <Grid item xs={12} sm={6}>
                        <Select
                            fullWidth
                            value={fitMode}
                            onChange={(e) => setFitMode(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="cover">맞춤: Cover</MenuItem>
                            <MenuItem value="contain">맞춤: Contain</MenuItem>
                            <MenuItem value="stretch">맞춤: Stretch(채우기)</MenuItem>
                        </Select>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="배율(%)"
                            type="number"
                            value={Math.round((Number(scale) || 1) * 100)}
                            onChange={(e) => {
                                const pct = nonneg(e.target.value);
                                setScale(clamp(pct / 100, 10 / 100, 500 / 100)); // 10%~500%
                            }}
                            fullWidth
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            helperText="10% ~ 500%"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="오프셋 X(px)"
                            type="number"
                            value={offsetX}
                            onChange={(e) => setOffsetX(toInt(e.target.value, 0))}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="오프셋 Y(px)"
                            type="number"
                            value={offsetY}
                            onChange={(e) => setOffsetY(toInt(e.target.value, 0))}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { reset(); onClose(); }}>취소</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={disabled || submitting}>
                    {submitting ? (initial?.id ? "수정 중..." : "등록 중...") : (initial?.id ? "수정" : "등록")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
